// UserPreferencesModal — 個人 UI 設定モーダル (consult 2026-05-17 確定、
// 2026-05-21 で 3→5 段階に拡張、2026-05-27 で radio → <select> プルダウン化
// + 6 段階・素の % 表示に変更, classic-sidebar-compaction.md §2.3).
//
// ClassicLayout 上部バー「表示」ボタンから開く. RoomSettingsModal とは
// 別物で **owner 権限不要、全員が開ける** 個人設定だけを扱う:
//   - 左サイドバーの拡縮 (6 段階: 72% / 85% / 100% / 110% / 125% / 140%)
//   - チャットの文字の大きさ (5 段階: 100% / 150% / 200% / 250% / 300%)
//     ← alpha-feedback-2026-05-31.md §3 (ID=E). 「チャット欄の文字が小さい、
//     300% まで大きくしたい」要望への応答. **行高 / 列幅は変えず文字サイズ
//     だけ** を em 倍率で拡縮するため、描画キャンバスを圧迫しない (= 仕様§3
//     「影響範囲はチャット欄のみ」要件をコードレベルで担保).
//
// 2026-06-13 続報 (consult): 同日初版で同居させた「ペン詳細」(手ぶれ補正 + AA) は
// **発見性のため独立** し、上部バー「ペン」ボタン → `PenSettingsModal` /
// `PenSettingsPanel` へ移動した (= 本モーダルは表示系設定に戻る)。
//
// 2026-06-27 続報 (consult): 同居していた描画ツール挙動 (ブラシカーソル / スポイトの
// 取得元) も `PenSettingsPanel`「描画の設定」へ移し、本モーダルは **純表示** (サイドバー
// の大きさ / チャット文字の大きさ) だけを扱う。表示と描画挙動の分類を明確にするため
// (= 旧「表示」に描画系が混在していて不自然だった)。
//
// 2026-05-31 chat-scale-cleanup: 旧「チャット欄の大きさ」(= wrapper 全体 zoom
// 10 段) は既存 splitter (chatRowPx / memberColPx の drag) と機能重複だった
// ため撤去. 代わりに「チャット文字サイズ」(本ファイル新規 select) を別軸として
// 追加した.
//
// 永続化: localStorage のみ (`oekaki:ui-scale-sidebar` /
// `oekaki:chat-font-scale`). サーバ同期なし、protocol 変更なし.
//
// UX:
//   - 選択 UI は **プルダウン (`<select>`)**. ラジオは段数を増やすと縦に伸びる
//     が、プルダウンは段数非依存 (= 5-6 段でも 1 行) なので省スペースに収まる
//     (classic-sidebar-compaction.md §3「なぜプルダウン % か」).
//   - option ラベルは **素の %**.「極小 / 標準」等の名称は廃止
//     (= naming 論争を排除、% は万人に通じる).
//   - 値変更は **即時反映** (Save ボタンなし). `onChange` を呼び出すと親
//     ClassicLayout 側で state が更新され、wrapper の `zoom` inline style /
//     ChatPanel の `fontScaleEm` prop に再 render で流れる.
//   - 親が `onChange` 時に同時に localStorage に書き込む (controlled パターン:
//     本 component は state を持たず props だけで決まる pure UI).
//   - close ボタン + 外クリック (backdrop) + Esc で閉じる (RoomSettingsModal
//     と同流儀).
//
// 設計判断: state を持たない controlled component にする理由 —
//   - ClassicLayout 側で `sidebarScale` / `chatFontScale` を保持しないと wrapper
//     の zoom / ChatPanel の fontScaleEm に流せない. 結局 lift-state-up が必要
//     なので, modal は <select> の見た目だけを担当する pure component に保つ.

import { useEffect, useId } from 'react';

import {
  type ChatFontScale,
  chatFontScaleToEm,
} from '../infrastructure/chatFontScalePreference.js';
import { type UiScale, uiScaleToCssZoom } from '../infrastructure/uiScalePreference.js';
import { SettingCheckboxRow } from './layouts/shared/SettingCheckboxRow.js';

export interface UserPreferencesModalProps {
  /** 現在の左サイドバー scale (controlled). */
  readonly sidebarScale: UiScale;
  /** 現在のチャット文字 scale (controlled). 2026-05-31 chat-scale-cleanup で
   * 旧 chatScale (= wrapper zoom) を置き換え. */
  readonly chatFontScale: ChatFontScale;
  /** サイドバー scale 変更時. 親側で setState + localStorage 書き込み. */
  readonly onSidebarScaleChange: (scale: UiScale) => void;
  /** チャット文字 scale 変更時. 親側で setState + localStorage 書き込み. */
  readonly onChatFontScaleChange: (scale: ChatFontScale) => void;
  /**
   * 「レイヤーのサムネイル」(layer-thumbnails-2026-10-13.md)。既定 ON、この端末のみ。
   * OFF = レイヤー欄は番号だけ (エンジンのサムネ処理も止まる)。未指定なら行ごと出さない。
   */
  readonly layerThumbnailsEnabled?: boolean;
  readonly onLayerThumbnailsEnabledChange?: (next: boolean) => void;
  /** 遅い端末でエンジンが自動停止した (そのタブの間は戻らない)。説明を出す。 */
  readonly layerThumbnailsStopped?: boolean;
  /** Modal を閉じる. */
  readonly onClose: () => void;
  /**
   * S3 スライス2 (floating-panels-2026-08-01.md §7 経路3「配置をリセット」):
   * Classic のフロート配置 (パレットパネル detach / チャット detach) を既定の
   * docked 状態へ戻す。Modern は自前のワークスペース preset ピッカー
   * (`ModernWorkspacePresetPicker`) が同役を既に持つため `undefined` を渡す
   * (= 本行自体を出さない、二重の救済導線を作らない)。
   */
  readonly onResetLayout?: () => void;
}

/** プルダウン option の表示順 + ラベル (UiScale 用). 値順は「最小 → 特大」固定. */
interface SidebarScaleOption {
  readonly value: UiScale;
  /** option に出すラベル. 素の % のみ (= 「極小 / 標準」等の名称は廃止). */
  readonly label: string;
}

/** プルダウン option (ChatFontScale 用). */
interface ChatFontScaleOption {
  readonly value: ChatFontScale;
  readonly label: string;
}

/**
 * CSS zoom (float) → 表示 % 文字列. `uiScaleToCssZoom` 結果は 0.72 / 0.85 /
 * 1.0 / 1.1 / 1.25 / 1.4 の固定値なので Math.round で % 化して安定表示する.
 */
const sidebarZoomLabel = (scale: UiScale): string =>
  `${Math.round(uiScaleToCssZoom(scale) * 100)}%`;

/** em (float) → 表示 % 文字列. ChatFontScale 用. */
const chatFontEmLabel = (scale: ChatFontScale): string =>
  `${Math.round(chatFontScaleToEm(scale) * 100)}%`;

// 2026-05-27 (classic-sidebar-compaction.md §2.3): radio → <select> プルダウン化
// + 6 段階. option ラベルは素の % のみ (= 72% / 85% / 100% / 110% / 125% / 140%).
// **サイドバー専用 6 段**: 上限 140% で据え置く.
// 2026-05-31 chat-scale-cleanup: 旧上振れ 4 段 (xxlarge / xxxlarge / huge /
// gigantic) は撤去された (= 「チャット欄の大きさ」軸を削除したため不要).
const SIDEBAR_SCALE_OPTIONS: readonly SidebarScaleOption[] = [
  { value: 'xxsmall', label: sidebarZoomLabel('xxsmall') },
  { value: 'xsmall', label: sidebarZoomLabel('xsmall') },
  { value: 'small', label: sidebarZoomLabel('small') },
  { value: 'medium', label: sidebarZoomLabel('medium') },
  { value: 'large', label: sidebarZoomLabel('large') },
  { value: 'xlarge', label: sidebarZoomLabel('xlarge') },
] as const;

// 2026-05-31 chat-scale-cleanup: 新規「チャット文字サイズ」専用. 上限 300% は
// ユーザ要望 (alpha-feedback §3 ID=E) で明示. 2026-06-05: 等倍より一段小さく
// したい要望で 85% を下側に追加 → 計 6 段 (85% / 100% / 150% / 200% / 250% / 300%).
const CHAT_FONT_SCALE_OPTIONS: readonly ChatFontScaleOption[] = [
  { value: 'x0_85', label: chatFontEmLabel('x0_85') },
  { value: 'x1', label: chatFontEmLabel('x1') },
  { value: 'x1_5', label: chatFontEmLabel('x1_5') },
  { value: 'x2', label: chatFontEmLabel('x2') },
  { value: 'x2_5', label: chatFontEmLabel('x2_5') },
  { value: 'x3', label: chatFontEmLabel('x3') },
] as const;

/**
 * Pure UI 部分 (hooks を使わない). props ですべてが決まる function なので
 * テストでは `UserPreferencesModalView({...})` を直接呼んで React tree を
 * 走査できる (= legacyFrames.test.ts と同流儀).
 *
 * 外側の `UserPreferencesModal` で `useId` / `useEffect` を捌き、ID と onClose
 * を本コンポーネントに props で渡す.
 */
export interface UserPreferencesModalViewProps extends UserPreferencesModalProps {
  /** Dialog 見出しの aria-labelledby 用 ID. */
  readonly titleId: string;
  /** サイドバー <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly sidebarLegendId: string;
  /** チャット文字 <select> の label 紐付け (aria-labelledby) 用 ID. */
  readonly chatFontLegendId: string;
}

export const UserPreferencesModalView = ({
  sidebarScale,
  chatFontScale,
  onSidebarScaleChange,
  onChatFontScaleChange,
  layerThumbnailsEnabled,
  onLayerThumbnailsEnabledChange,
  layerThumbnailsStopped = false,
  onClose,
  onResetLayout,
  titleId,
  sidebarLegendId,
  chatFontLegendId,
}: UserPreferencesModalViewProps): React.ReactNode => (
  <div className="fixed inset-0 z-50 grid place-items-center p-4">
    {/* Backdrop. RoomSettingsModal と同じく a11y compliant な button にする
        (= キーボード操作からも閉じられる, role=button + Esc 経由). */}
    <button
      type="button"
      aria-label="閉じる"
      tabIndex={-1}
      onClick={onClose}
      className="-z-10 absolute inset-0 cursor-default bg-black/60"
    />
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      // w-full max-w: 固定 w-[360px] はスマホ縦 (390px で親 p-4 引き後 358px / 360px
      // 端末で 328px) を超えてはみ出す (2026-07-03 一括点検)。
      className="max-h-[90vh] w-full max-w-[360px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 id={titleId} className="text-base font-bold text-[var(--color-accent)]">
          表示の設定
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          className="rounded px-2 py-1 text-sm text-[var(--color-text-muted)] hover:bg-white/10"
        >
          ✕
        </button>
      </div>
      <p className="mb-4 text-[11px] text-[var(--color-text-muted)]">
        この端末でのみ保存される個人設定です。ルームの他の参加者には影響しません。
      </p>
      <SidebarScaleSelect
        legend="サイドバーの大きさ"
        legendId={sidebarLegendId}
        value={sidebarScale}
        onChange={onSidebarScaleChange}
        options={SIDEBAR_SCALE_OPTIONS}
      />
      <hr className="my-4 border-white/10" />
      {/* alpha-feedback-2026-05-31.md §3 (ID=E) chat-scale-cleanup: 「チャット欄
          の大きさ」(= wrapper 全体 zoom) は splitter と機能重複だったため撤去し、
          代わりに「チャットの文字の大きさ」を別軸として追加. 行高 / 列幅は変えず
          に log + input + popup input の文字だけ em 倍率で拡縮するため、
          描画キャンバスを圧迫しない. 上限 300% は要望そのまま. */}
      <ChatFontScaleSelect
        legend="チャットの文字の大きさ"
        legendId={chatFontLegendId}
        value={chatFontScale}
        onChange={onChatFontScaleChange}
        options={CHAT_FONT_SCALE_OPTIONS}
      />
      {/* レイヤーのサムネイル (layer-thumbnails-2026-10-13.md)。純表示の個人設定なのでここ。
          自動停止中 (遅い端末) はチェックを ON にしても戻らないので、その旨を説明に出す。 */}
      {layerThumbnailsEnabled !== undefined && onLayerThumbnailsEnabledChange && (
        <>
          <hr className="my-4 border-white/10" />
          <SettingCheckboxRow
            label="レイヤーのサムネイル"
            description={
              layerThumbnailsStopped
                ? 'この端末では動作を軽くするため止めています（このタブを開き直すまで戻りません）'
                : 'レイヤー欄に小さな絵を出します。OFF にすると番号だけになります'
            }
            checked={layerThumbnailsEnabled}
            onChange={onLayerThumbnailsEnabledChange}
          />
        </>
      )}
      {/* 2026-06-13 続報: 旧「ペン詳細」(手ぶれ補正 / AA) は上部バー「描画」ボタン →
          PenSettingsModal へ移動。2026-06-27 続報: ブラシカーソル / スポイトの取得元も
          同モーダル (描画の設定) へ移し、本モーダルは純表示 (サイドバー / チャット文字) のみ。 */}
      {/* S3 スライス2 (floating-panels-2026-08-01.md §7 経路3): Classic のみ表示
          (`onResetLayout` が渡されたときだけ)。フロート配置 (パレット detach /
          チャット detach) を既定の docked へ戻す「迷子からの復帰」導線
          (Modern のワークスペース preset ピッカーに相当)。 */}
      {onResetLayout && (
        <>
          <hr className="my-4 border-white/10" />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-[var(--color-text)]">パネルの配置</span>
            <button
              type="button"
              onClick={onResetLayout}
              className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs font-bold text-[var(--color-text)] hover:bg-white/10"
            >
              配置をリセット
            </button>
          </div>
        </>
      )}
    </div>
  </div>
);

export const UserPreferencesModal = (props: UserPreferencesModalProps): React.ReactNode => {
  const titleId = useId();
  const sidebarLegendId = useId();
  const chatFontLegendId = useId();

  // Esc で閉じる (RoomSettingsModal と同パターン).
  const { onClose } = props;
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [onClose]);

  return (
    <UserPreferencesModalView
      {...props}
      titleId={titleId}
      sidebarLegendId={sidebarLegendId}
      chatFontLegendId={chatFontLegendId}
    />
  );
};

/**
 * 1 段のスケール選択 (= ラベル + `<select>` プルダウン、サイドバー UiScale 用).
 * 2026-05-27 で radio グループから置換 (classic-sidebar-compaction.md §2.3).
 * プルダウンは段数非依存なので 6 段でも縦 1 行に収まり省スペース.
 * option ラベルは素の % のみ.
 *
 * `<label htmlFor>` を使わず `aria-labelledby` で見出し <span> に紐付ける
 * (= 2 段並ぶので id を一意化).
 */
const SidebarScaleSelect = ({
  legend,
  legendId,
  value,
  onChange,
  options,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: UiScale;
  readonly onChange: (next: UiScale) => void;
  readonly options: readonly SidebarScaleOption[];
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        // value attr から UiScale を narrow し直して伝搬する. <select> の値は
        // 必ず option の value のどれかなので isUiScale 検証は不要だが、型を
        // 締めるため as cast ではなく guard は使わず直接 narrow できる string を渡す.
        onChange(e.target.value as UiScale);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

/**
 * チャット文字サイズ用 <select> (ChatFontScale 用). UI は SidebarScaleSelect と
 * 揃える. 2026-05-31 chat-scale-cleanup で導入した独立軸 (= 仕様 §3 ID=E).
 */
const ChatFontScaleSelect = ({
  legend,
  legendId,
  value,
  onChange,
  options,
}: {
  readonly legend: string;
  readonly legendId: string;
  readonly value: ChatFontScale;
  readonly onChange: (next: ChatFontScale) => void;
  readonly options: readonly ChatFontScaleOption[];
}): React.ReactNode => (
  <div className="flex items-center justify-between gap-2">
    <span id={legendId} className="text-xs font-bold text-[var(--color-text)]">
      {legend}
    </span>
    <select
      aria-labelledby={legendId}
      value={value}
      onChange={(e) => {
        onChange(e.target.value as ChatFontScale);
      }}
      className="rounded border border-white/10 bg-[var(--color-bg-soft)] px-2 py-1 text-xs text-[var(--color-text)]"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);
